import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaUserCircle,
  FaClock,
  FaBell,
  FaUsers,
  FaBars,
  FaHome,
  FaBriefcase,
  FaTasks,
  FaUserFriends,
  FaChartLine,
  FaFileInvoiceDollar,
  FaExchangeAlt,
  FaReceipt,
  FaSignOutAlt,
} from 'react-icons/fa';
import './NavBar.css';
import {
  InteractionRequiredAuthError,
  AuthenticationResult,
} from '@azure/msal-browser';
import Timer from '../Timer/Timer';
import {
  isLegalEnvironment,
  isFeatureEnabled,
} from '@frontend/AppSettings/environmentConfig';
import useMsalInstance from '@frontend/AppSettings/hooks/useMsalInstance';
import { useRuntimeConfig } from '@frontend/__settings__/runtimeConfig';
import useActiveAccount from '@frontend/AppSettings/hooks/useActiveAccount';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import PersonalWidget from '../Profile/Components/PersonalWidget/PersonalWidget';
import { useBidCapabilities } from '../BidSheets/useBidCapabilities';

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const NavBarAuth: React.FC = () => {
  const instance = useMsalInstance();
  const account = useActiveAccount();
  const location = useLocation();
  const employeeId = useActiveEmployeeId();
  const [, setAccessToken] = useState<string | null>(null);
  const [showTimer, setShowTimer] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [showProfileDropdown, setShowProfileDropdown] = useState<boolean>(false);
  const [permishId, setPermishId] = useState<string | number | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);
  // Subscribed for the SIDE EFFECT, not the value: resolving capabilities is what pushes money
  // visibility into financialDisplay's module state, and this component is mounted for the app's
  // lifetime so it is the reliable place to do it. The value itself is no longer read here — the
  // reference-data entry moved to a cog on the bids page, which reads capabilities itself.
  // (Do not "clean this up" by deleting the call: money would stay redacted for everyone.)
  useBidCapabilities();
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  // Runtime config (P1): brand label comes from the tenant row; 'SEPT.' is the product
  // default when no row (or a NULL brand) exists. Client names never appear in code —
  // a client-branded deployment sets tenants.brand_label at rollout (owner decision,
  // 2026-08-23). Subscribing here also makes the nav re-evaluate its flag-gated items
  // (e.g. Bids) when the config arrives.
  const runtimeConfig = useRuntimeConfig();
  const brandLabel = runtimeConfig?.tenant?.brandLabel ?? 'SEPT.';
  const entityRoute = isLegalEnvironment ? '/matters' : '/projects';
  const entityLabel = isLegalEnvironment ? 'Matters' : 'Projects';

  const toggleTimer = (): void => {
    setShowTimer(!showTimer);
  };

  const toggleSidebar = (): void => {
    setSidebarOpen((prev) => !prev);
  };

  const toggleProfileDropdown = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setShowProfileDropdown((prev) => !prev);
  };

  const handleSignOut = (): void => {
    setShowProfileDropdown(false);
    // Same auth-bypass seam as the token effect below: under REACT_APP_E2E the account is synthetic
    // and the Azure ids are dummies, so logoutRedirect would send the browser to Microsoft
    // instead of signing anyone out.
    if (process.env.REACT_APP_E2E === 'true') return;
    // postLogoutRedirectUri ('/') comes from msalConfig. Passing the account scopes the cache
    // clear to it; omitting it would clear every cached account.
    instance.logoutRedirect({ account }).catch((error) => {
      console.error('Error signing out:', error);
    });
  };

  // When a person has no permission attached, default to the most restrictive
  // view (permission level 3).
  const resolvePermissionId = (permId: number | string | null): number => {
    const permIdNum = typeof permId === 'string' ? parseInt(permId, 10) : permId;
    if (permIdNum === null || permIdNum === undefined || Number.isNaN(permIdNum)) {
      return 3;
    }
    return permIdNum;
  };

  const getLedgerItemsForPermission = (permId: number | string | null): NavItem[] => {
    if (!permissionsLoaded) return [];
    const permIdNum = resolvePermissionId(permId);
    if (permIdNum === 2 || permIdNum === 3) {
      return [
        { label: 'Timesheets', to: '/financials/billing', icon: <FaClock /> },
      ];
    }
    return [
      { label: 'Financials', to: '/financials', icon: <FaChartLine /> },
      {
        label: 'Transactions',
        to: '/financial/transactions',
        icon: <FaExchangeAlt />,
      },
      { label: 'Expenses', to: '/financial/expenses', icon: <FaReceipt /> },
    ];
  };

  const getOrbitItemsForPermission = (permId: number | string | null): NavItem[] => {
    if (!permissionsLoaded) return [];
    const permIdNum = resolvePermissionId(permId);
    if (permIdNum === 3) return [];
    return [
      { label: 'CRM', to: '/sept-orbit?tab=individuals', icon: <FaUserFriends /> },
    ];
  };

  const legalNav: NavSection[] = [
    {
      title: 'Legal Operations',
      items: [
        { label: 'Dashboard', to: '/', icon: <FaHome /> },
        { label: entityLabel, to: entityRoute, icon: <FaBriefcase /> },
        { label: 'Tasks', to: '/tasks', icon: <FaTasks /> },
        { label: 'Time Tracking', to: '/Timer', icon: <FaClock /> },
      ],
    },
    {
      title: 'SEPT Orbit',
      items: getOrbitItemsForPermission(permishId),
    },
    {
      title: 'SEPT Ledger',
      items: getLedgerItemsForPermission(permishId),
    },
  ];

  const engineeringNav: NavSection[] = [
    {
      title: 'Workspace',
      items: [
        { label: 'Dashboard', to: '/', icon: <FaHome /> },
        { label: entityLabel, to: entityRoute, icon: <FaBriefcase /> },
        { label: 'Tasks', to: '/tasks', icon: <FaTasks /> },
        ...(isFeatureEnabled('bids')
          ? [{ label: 'Bids', to: '/bids', icon: <FaFileInvoiceDollar /> }]
          : []),
      ],
    },
    {
      title: 'SEPT Orbit',
      items: getOrbitItemsForPermission(permishId),
    },
    {
      title: 'SEPT Ledger',
      items: getLedgerItemsForPermission(permishId),
    },
    {
      title: 'Settings',
      items: [
        { label: 'Team', to: '/team', icon: <FaUsers /> },
      ],
    },
  ];

  const navSections = (isLegalEnvironment ? legalNav : engineeringNav).filter(
    (section) => section.items.length > 0,
  );

  useEffect(() => {
    if (!instance || !account) {
      setAccessToken(null);
      return;
    }

    // E2E / local-dev seam: never attempt MSAL when auth is bypassed. Same guard, and the same
    // reasoning, as getAccessToken (AppSettings/services/interceptors/axiosInterceptors.ts) and
    // getMicrosoftGraphToken (…/microsoftGraphInterceptors.ts) — this was the one MSAL call site
    // that missed it.
    //
    // Without the guard: index.tsx injects a synthetic account under REACT_APP_E2E, so `account` is
    // truthy, this effect runs against the dummy Azure client/tenant ids, the silent iframe fails
    // with InteractionRequiredAuthError, and the fallback opens an **interactive popup** to
    // sts.microsoftonline.com. In `npm run sept:frontend` that popup lands on
    // "This site can't be reached" over a working app, which looks like a broken login and isn't.
    // Nothing to set: the token is written to state that is never read (`const [, setAccessToken]`
    // above), so this effect's only observable behaviour is the MSAL call itself. Storing a fake
    // token here would cost a render and mean nothing. The dead state is left alone rather than
    // removed in the same change as a bug fix — see the note in the commit.
    if (process.env.REACT_APP_E2E === 'true') return;

    const request = {
      scopes: ['user.read.all'],
      account,
    };

    instance
      .acquireTokenSilent(request)
      .then((response: AuthenticationResult) => {
        setAccessToken(response.accessToken);
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          instance
            .acquireTokenPopup(request)
            .then((response: AuthenticationResult) => {
              setAccessToken(response.accessToken);
            })
            .catch(() => {
              setAccessToken(null);
            });
        } else {
          setAccessToken(null);
        }
      });
  }, [instance, account]);

  useEffect(() => {
    const fetchEmployeePermission = async () => {
      if (!employeeId) return;
      try {
        const employeeData = await EmployeeService.getEmployeeById(employeeId);
        if (employeeData?.permish_id !== undefined) {
          setPermishId(employeeData.permish_id);
        }
      } catch (error) {
        console.error('Error fetching employee permission:', error);
      } finally {
        setPermissionsLoaded(true);
      }
    };
    fetchEmployeePermission();
  }, [employeeId]);

  useEffect(() => {
    const paddingLeft = sidebarOpen ? '240px' : '80px';
    document.body.style.paddingLeft = paddingLeft;
    document.body.style.paddingTop = '76px';
    return () => {
      document.body.style.paddingLeft = '0px';
      document.body.style.paddingTop = '0px';
    };
  }, [sidebarOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Segment-boundary matching: '/bids' owns '/bids' and '/bids/…' but never a
    // sibling like '/bidsheets'. A '?query' link matches only with that query present.
    const [pathBase, pathQuery] = path.split('?');
    if (pathQuery) {
      return location.pathname === pathBase && location.search.includes(pathQuery);
    }
    return location.pathname === pathBase || location.pathname.startsWith(pathBase + '/');
  };

  return (
    <>
      <div
        className={`nav-shell ${sidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}
      >
        <aside className={`nav-sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
          <div className="nav-sidebar-header">
          <button className="nav-icon-button ghost" onClick={toggleSidebar}>
              <FaBars />
            </button>

            <div className="nav-brand-mark">{brandLabel[0]}.</div>
            {sidebarOpen && <span className="nav-brand-name">{brandLabel}</span>}
          </div>
          <div className="nav-sidebar-sections">
            {navSections.map((section) => (
              <div className="nav-sidebar-section" key={section.title}>
                {sidebarOpen && (
                  <div className="nav-section-title">{section.title}</div>
                )}
                <div className="nav-section-items">
                  {section.items.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={`nav-item ${
                        isActive(item.to) ? 'active' : ''
                      }`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {sidebarOpen && <span className="nav-label">{item.label}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>

        <header className="nav-topbar">
          <div className="nav-topbar-left">
          </div>

          <div className="nav-topbar-actions">
            {isLegalEnvironment && (
              <button onClick={toggleTimer} className="nav-icon-button ghost">
                <FaClock />
              </button>
            )}
            <Link to="/notifications" className="nav-icon-button ghost">
              <FaBell />
            </Link>
            <div className="nav-profile-dropdown-container" ref={profileDropdownRef}>
              <button
                ref={profileButtonRef}
                onClick={toggleProfileDropdown}
                className="nav-profile-chip"
                type="button"
                aria-expanded={showProfileDropdown}
                aria-haspopup="true"
              >
                <FaUserCircle className="nav-profile-icon" />
                <div className="nav-profile-meta">
                  <span className="nav-profile-name">
                    {account?.name ?? 'Profile'}
                  </span>
                  <span className="nav-profile-role">View profile</span>
                </div>
              </button>
              {showProfileDropdown && (
                <div 
                  className="nav-profile-dropdown" 
                  role="menu"
                  style={{
                    position: 'fixed',
                    top: profileButtonRef.current 
                      ? `${profileButtonRef.current.getBoundingClientRect().bottom + 8}px`
                      : '84px',
                    right: profileButtonRef.current
                      ? `${window.innerWidth - profileButtonRef.current.getBoundingClientRect().right}px`
                      : '24px',
                    zIndex: 1003,
                  }}
                >
                  <PersonalWidget />
                  <div className="nav-profile-dropdown-footer">
                    <Link
                      to="/profile"
                      className="nav-profile-dropdown-link"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      View Full Profile →
                    </Link>
                    <button
                      type="button"
                      className="nav-profile-signout"
                      onClick={handleSignOut}
                    >
                      <FaSignOutAlt />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      </div>

      {showTimer && isLegalEnvironment && <Timer />}
    </>
  );
};

export default NavBarAuth;
